import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Store, MapPin, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { t } from "../lib/i18n";
import { partnerApi } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const PartnerRegisterPage = () => {
  const { tr } = useLang();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", shop_name: "", mobile: "", email: "",
    password: "", address: "", callmebot_key: "",
    lat: null, lng: null,
  });
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const autoLocate = () => {
    if (!navigator.geolocation) {
      toast.error(tr({ en: "Geolocation not supported", hi: "स्थान समर्थित नहीं" }));
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
          { headers: { "Accept-Language": "en" } },
        );
        const data = await res.json();
        setForm((f) => ({ ...f, lat: latitude, lng: longitude, address: data.display_name || f.address }));
        toast.success(tr({ en: "Location captured", hi: "स्थान प्राप्त हुआ" }));
      } catch (err) {
        setForm((f) => ({ ...f, lat: latitude, lng: longitude }));
      } finally {
        setLocating(false);
      }
    }, (err) => {
      toast.error(err.message);
      setLocating(false);
    }, { enableHighAccuracy: true, timeout: 10000 });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.shop_name || !form.mobile || !form.password || !form.address) {
      toast.error(tr({ en: "Please fill all required fields", hi: "कृपया सभी आवश्यक फ़ील्ड भरें" }));
      return;
    }
    if (form.password.length < 6) {
      toast.error(tr({ en: "Password must be 6+ characters", hi: "पासवर्ड कम से कम 6 अक्षर" }));
      return;
    }
    setSubmitting(true);
    try {
      await partnerApi.post("/retailers/register", form);
      toast.success(tr({ en: "Registration submitted. Awaiting admin approval.", hi: "आवेदन जमा। एडमिन स्वीकृति की प्रतीक्षा।" }));
      setTimeout(() => navigate("/partner/login"), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partner-register-page" className="max-w-2xl mx-auto px-4 lg:px-8 py-12">
      <div className="rounded-xl bg-white border border-slate-200 p-6 sm:p-8">
        <div className="w-12 h-12 rounded-md bg-teal-50 text-teal-700 flex items-center justify-center mb-5">
          <Store className="w-6 h-6" />
        </div>
        <h1 className="text-2xl font-heading font-semibold text-slate-900">{tr(t.partner.register)}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {tr({ en: "Join Ashish Point's B2B retailer network. Admin will approve your account.", hi: "आशीष पॉइंट के B2B रिटेलर नेटवर्क से जुड़ें। एडमिन आपके खाते को स्वीकृत करेंगे।" })}
        </p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label={tr(t.common.name)} req>
              <Input value={form.name} onChange={change("name")} data-testid="partner-reg-name" />
            </Field>
            <Field label={tr(t.partner.shopName)} req>
              <Input value={form.shop_name} onChange={change("shop_name")} data-testid="partner-reg-shop" />
            </Field>
            <Field label={tr(t.common.mobile)} req>
              <Input inputMode="tel" value={form.mobile} onChange={change("mobile")} placeholder="+91 …" data-testid="partner-reg-mobile" />
            </Field>
            <Field label={tr(t.common.email)}>
              <Input type="email" value={form.email} onChange={change("email")} data-testid="partner-reg-email" />
            </Field>
          </div>

          <Field label={tr(t.common.address)} req>
            <div className="flex flex-col sm:flex-row gap-2">
              <Textarea rows={2} value={form.address} onChange={change("address")} className="flex-1" data-testid="partner-reg-address" />
              <Button type="button" variant="outline" onClick={autoLocate} disabled={locating} className="shrink-0 sm:self-start" data-testid="partner-reg-autoloc">
                {locating ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4 mr-2" />}
                {tr(t.partner.autoLoc)}
              </Button>
            </div>
            {form.lat && (
              <p className="mt-1 text-xs text-slate-500">📍 {form.lat.toFixed(5)}, {form.lng.toFixed(5)}</p>
            )}
          </Field>

          <Field label={tr(t.admin.password)} req>
            <Input type="password" value={form.password} onChange={change("password")} data-testid="partner-reg-password" />
          </Field>

          <Field label={tr({ en: "CallMeBot API Key (optional — for auto WhatsApp alerts)", hi: "CallMeBot API Key (वैकल्पिक — WhatsApp alert हेतु)" })}>
            <Input value={form.callmebot_key} onChange={change("callmebot_key")} placeholder="Get from wa.me/34644663268" data-testid="partner-reg-callmebot" />
          </Field>

          <Button type="submit" disabled={submitting} className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white" data-testid="partner-reg-submit">
            {submitting ? tr(t.common.submitting) : tr(t.partner.register)}
          </Button>
          <p className="text-center text-sm text-slate-500">
            {tr({ en: "Already a partner?", hi: "पहले से पार्टनर हैं?" })}{" "}
            <Link to="/partner/login" className="text-teal-700 hover:underline">{tr(t.partner.login)}</Link>
          </p>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, req, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-800">
      {label} {req && <span className="text-orange-600">*</span>}
    </Label>
    <div className="mt-1.5">{children}</div>
  </div>
);

export default PartnerRegisterPage;
